import { ChartTooltipContent } from "@/components/Chart";
import { BaseInsightLineChart } from "@/components/insights/BaseInsightLineChart";
import { PRIMARY_AXIS_COLOR } from "@/components/insights/insights-constants";
import { useEnvironmentRuntimeTimeSeries } from "@/queries/insights-queries";
import { formatChartDate, type TimeRange } from "@/routes/insights/insights";
import { type Timestamp } from "@bufbuild/protobuf";
import { useMemo, type FC } from "react";
import * as RechartsPrimitive from "recharts";
import type { ValueType } from "recharts/types/component/DefaultTooltipContent";

const unit = "hour";

type PointPayload = {
    timestamp: Timestamp;
    runtime: number;
    date: Date;
};

type Props = {
    startTimestamp: Timestamp;
    endTimestamp: Timestamp;
    selectedRange: TimeRange;
    projectId?: string;
};
export const EnvironmentRuntimeLineChart: FC<Props> = ({ startTimestamp, endTimestamp, selectedRange, projectId }) => {
    const { data, resolution, isLoading } = useEnvironmentRuntimeTimeSeries(
        startTimestamp,
        endTimestamp,
        selectedRange,
        projectId,
    );

    const chartData = useMemo(() => {
        if (!data?.totalRuntime) return [];

        const chartData = data.totalRuntime.map((point) => {
            const dateObj = point.time ? point.time.toDate() : new Date();
            const runtime = point.value ? Number(point.value) / 60 / 60 : 0;

            return {
                date: dateObj,
                timestamp: dateObj.getTime(),
                runtime,
            };
        });

        return chartData;
    }, [data]);

    return (
        <div className="w-full">
            <BaseInsightLineChart
                isLoading={isLoading}
                chartData={chartData}
                chartConfig={{
                    runtime: {
                        label: "Runtime",
                        color: PRIMARY_AXIS_COLOR,
                    },
                }}
                yAxisConfig={{
                    width: 50,
                    tickFormatter: (value: number) => `${value.toLocaleString()} h`,
                    domain: [0, "auto"],
                    minTickGap: 20,
                    allowDecimals: false,
                }}
                tooltipContent={
                    <ChartTooltipContent
                        labelFormatter={(_: string | number | Date, payload: unknown[]) => {
                            if (!payload || payload.length === 0) return "";
                            const item = payload[0] as { payload: PointPayload };
                            if (!(item.payload.date instanceof Date)) return "";

                            return formatChartDate(
                                item.payload.date,
                                resolution,
                                startTimestamp,
                                endTimestamp,
                            );
                        }}
                        valueFormatter={(value: ValueType) => {
                            const runtime = value as number;
                            const rounded = Math.round(runtime * 100) / 100; // round to 2 decimals

                            return `${rounded.toLocaleString()} ${unit}${rounded === 1 ? "" : "s"}`;
                        }}
                    />
                }
            >
                <RechartsPrimitive.Line
                    type="monotone"
                    dataKey="runtime"
                    unit="hours"
                    name="runtime"
                    stroke="var(--color-runtime)"
                    strokeWidth={2}
                    dot={{ r: 0 }}
                    activeDot={{
                        r: 6,
                        stroke: "var(--color-runtime)",
                        strokeWidth: 2,
                        fill: "rgb(var(--ona-orange-100))",
                    }}
                    xAxisId="0"
                />
            </BaseInsightLineChart>
        </div>
    );
};
